import { format, startOfWeek, endOfWeek, addDays } from 'date-fns';
import { BRANDS } from '../../../types/brands';

// Interface for country data
interface Country {
  name: string;
}

// Interface for special event data
interface SpecialEvent {
  date_value: string;
  specialEventReason?: number | string;
}

// Interface for special event reason
interface SpecialEventReason {
  id: number | string;
  name: string;
}

export const getWeekStartAndEnd = (weekDate: Date) => {
  const startOfWeekDate = startOfWeek(weekDate, { weekStartsOn: 1 }); // Monday as start of week
  const endOfWeekDate = endOfWeek(weekDate, { weekStartsOn: 1 }); // Sunday as end of week

  return {
    startDate: format(startOfWeekDate, 'yyyy-MM-dd'),
    endDate: format(endOfWeekDate, 'yyyy-MM-dd'),
  };
};

export function getWeekDays(selectedWeek: Date): Date[] {
  const days: Date[] = [];
  const startOfWeekDate = startOfWeek(selectedWeek, { weekStartsOn: 1 }); // Monday as start of week

  for (let i = 0; i < 7; i++) {
    const currentDay = addDays(startOfWeekDate, i);
    days.push(currentDay);
  }
  return days;
}

export function formatTime(time: string | null, is12HFormat = true) {
  if (!time) return null;
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);

  if (is12HFormat) {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  } else {
    return `${hours}:${minutes}`;
  }
}

export function isPLKAutoCalculatedLateNight(
  brand: BRANDS,
  Region: string,
  countries: Country[],
) {
  return (
    brand === BRANDS.PLK &&
    (Region === 'NA' ||
      (countries?.length > 0 &&
        countries?.every(val => val.name?.split(' - ')[0] === 'NA')))
  );
}

export function findSpecialEventDataForDate(
  specialEventDayByDay: SpecialEvent[],
  targetDate: Date,
) {
  if (!specialEventDayByDay || !Array.isArray(specialEventDayByDay)) {
    return null;
  }

  const dateString = format(targetDate, 'yyyy-MM-dd');
  return specialEventDayByDay.find(event => event.date_value === dateString);
}

export function findSpecialEventReasonName(
  specialEventReasons: SpecialEventReason[],
  reasonId: number | string,
) {
  if (!specialEventReasons || !Array.isArray(specialEventReasons)) {
    return null;
  }

  const reason = specialEventReasons.find(r => r.id === reasonId);
  return reason ? reason.name : null;
}

export function getTruncatedTextProps(text: string, maxLength = 20) {
  const isTruncated = text.length > maxLength;
  return {
    displayText: isTruncated ? `${text.substring(0, maxLength)}...` : text,
    tooltipTitle: isTruncated ? text : undefined,
  };
}

export const dayNames = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

// Weekday names in the format used by BaseAdjustment: "1 - Sunday", "2 - Monday", etc.
export const weekdayNames = [
  '1 - Sunday',
  '2 - Monday',
  '3 - Tuesday',
  '4 - Wednesday',
  '5 - Thursday',
  '6 - Friday',
  '7 - Saturday',
];

export function isWeekend(dayIndex: number): boolean {
  return dayIndex >= 5; // Saturday (5) and Sunday (6) are weekend days
}

/**
 * Calculates border radius styles for items in a list based on their position
 * @param currentIndex - The index of the current item
 * @param totalItems - The total number of items in the list
 * @param radius - The border radius value (default: '4px')
 * @returns Object with border radius styles
 */
export function getBorderRadiusStyles(
  currentIndex: number,
  totalItems: number,
  radius = '4px',
) {
  return {
    borderTopLeftRadius: currentIndex === 0 ? radius : 0,
    borderTopRightRadius: currentIndex === 0 ? radius : 0,
    borderBottomLeftRadius: currentIndex === totalItems - 1 ? radius : 0,
    borderBottomRightRadius: currentIndex === totalItems - 1 ? radius : 0,
  };
}
